import { Alert, Button, Link, Stack, TextField } from "@mui/material";
import { useEffect, useState, type FormEvent } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";

import { api, ensureCsrf, errorMessage } from "../api/client";
import { AuthLayout } from "./AuthLayout";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    await ensureCsrf();
    const { error: err } = await api.POST("/api/v1/accounts/auth/password/forgot/", { body: { email } });
    if (err) setError(errorMessage(err));
    else setSent(true);
  }
  return (
    <AuthLayout title="Reset your password" subtitle="We will email you a link to choose a new password."
      footer={<Link component={RouterLink} to="/login">Back to sign in</Link>}>
      {sent ? <Alert severity="success">If an account exists for {email}, a reset link is on its way.</Alert> : (
        <form onSubmit={onSubmit}>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
            <Button type="submit" variant="contained" size="large">Send reset link</Button>
          </Stack>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPassword() {
  const { uid = "", token = "" } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords do not match."); return; }
    await ensureCsrf();
    const { error: err } = await api.POST("/api/v1/accounts/auth/password/reset/", { body: { uid, token, new_password: password } });
    if (err) setError(errorMessage(err));
    else { setDone(true); setTimeout(() => navigate("/login"), 1500); }
  }
  return (
    <AuthLayout title="Choose a new password">
      {done ? <Alert severity="success">Password updated. Redirecting to sign in…</Alert> : (
        <form onSubmit={onSubmit}>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="New password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus autoComplete="new-password" helperText="At least 10 characters, not too common." />
            <TextField label="Confirm password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password" />
            <Button type="submit" variant="contained" size="large">Update password</Button>
          </Stack>
        </form>
      )}
    </AuthLayout>
  );
}

export function VerifyEmail() {
  const { uid = "", token = "" } = useParams();
  const [state, setState] = useState<"working" | "ok" | "error">("working");
  const [message, setMessage] = useState("");
  useEffect(() => {
    void (async () => {
      await ensureCsrf();
      const { error } = await api.POST("/api/v1/accounts/auth/verify/confirm/", { body: { uid, token } });
      if (error) { setState("error"); setMessage(errorMessage(error)); } else setState("ok");
    })();
  }, [uid, token]);
  return (
    <AuthLayout title="Email verification" footer={<Link component={RouterLink} to="/app">Continue to the app</Link>}>
      {state === "working" && <Alert severity="info">Verifying…</Alert>}
      {state === "ok" && <Alert severity="success">Your email address is verified. Thank you!</Alert>}
      {state === "error" && <Alert severity="error">{message}</Alert>}
    </AuthLayout>
  );
}
